import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from './lib/supabaseClient'
import { sendPasswordResetEmail } from './lib/emailService' 

function Login() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isSignUp, setIsSignUp] = useState(false)
  const [isForgotPassword, setIsForgotPassword] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [isPasskeyMode, setIsPasskeyMode] = useState(false)
  const [passkeyStep, setPasskeyStep] = useState('login') // 'login' | 'register'

  const handleSuccessfulLogin = async (userId, userEmail) => {
    try {
      console.log('🔍 Login successful for user:', userId);
      console.log('🔍 Email:', userEmail);
      
      const { data: userData, error } = await supabase
        .from('users')
        .select('role, company_id, onboarding_completed')
        .eq('id', userId)
        .maybeSingle()

      if (error) {
        console.error('❌ Error fetching user role:', error);
        navigate('/onboarding');
        return;
      }

      console.log('✅ User data from database:', userData);

      if (!userData || !userData.role) {
        console.log('⚠️ No role found, redirecting to onboarding');
        navigate('/onboarding');
        return;
      }

      if (!userData.onboarding_completed) {
        console.log('⚠️ Onboarding not completed, redirecting to onboarding');
        navigate('/onboarding');
        return;
      }

      const role = userData.role;
      console.log('✅ Role found:', role);

      switch(role) {
        case 'leadership':
          console.log('➡️ Redirecting to leadership portal');
          navigate('/leadership');
          break
        case 'accounting':
          console.log('➡️ Redirecting to accounting portal');
          navigate('/accounting');
          break
        case 'marketing':
          console.log('➡️ Redirecting to marketing portal');
          navigate('/marketing');
          break
        case 'legal':
          console.log('➡️ Redirecting to legal portal');
          navigate('/legal');
          break
        case 'recruitment':
          console.log('➡️ Redirecting to recruitment portal');
          navigate('/recruitment');
          break
        case 'cro':
          console.log('➡️ Redirecting to cro portal');
          navigate('/cro');
          break
        default:
          console.log('➡️ Redirecting to agent portal (default)');
          navigate('/agent')
      }
    } catch (err) {
      console.error('❌ Error in handleSuccessfulLogin:', err);
      navigate('/agent')
    }
  }

  // ============ PASSKEY FUNCTIONS ============
  const checkPasskeySupport = () => {
    return typeof window !== 'undefined' && 
           'credentials' in navigator && 
           'create' in navigator.credentials &&
           'get' in navigator.credentials;
  }

  const handlePasskeyLogin = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    if (!checkPasskeySupport()) {
      setError('Your browser does not support passkeys. Please use email/password or Google login.');
      return;
    }

    setPasskeyLoading(true);
    setError('');
    setSuccess('');

    try {
      // First, get the user's passkey factors
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
      
      if (factorsError) {
        console.error('Factors error:', factorsError);
        throw new Error('Could not retrieve passkey factors. Please try again.');
      }

      const passkeyFactors = factorsData?.webauthn || [];
      
      if (passkeyFactors.length === 0) {
        setError('No passkey found. Please register a passkey first.');
        setPasskeyLoading(false);
        return;
      }

      // Use the first passkey factor
      const passkeyFactor = passkeyFactors[0];
      console.log('Using passkey factor:', passkeyFactor.id);

      // Challenge the factor
      const { data: challengeData, error: challengeError } = 
        await supabase.auth.mfa.challenge({
          factorId: passkeyFactor.id
        });

      if (challengeError) {
        console.error('Challenge error:', challengeError);
        throw new Error('Failed to create authentication challenge.');
      }

      // WebAuthn get assertion
      const publicKeyCredentialRequestOptions = {
        challenge: Uint8Array.from(atob(challengeData.challenge), c => c.charCodeAt(0)),
        timeout: 60000,
        rpId: window.location.hostname,
        userVerification: 'required',
      };

      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions
      });

      // Verify the assertion
      const { data: verifyData, error: verifyError } = 
        await supabase.auth.mfa.verify({
          factorId: passkeyFactor.id,
          challengeId: challengeData.id,
          authenticatorData: new Uint8Array(assertion.response.authenticatorData),
          clientDataJSON: new Uint8Array(assertion.response.clientDataJSON),
          signature: new Uint8Array(assertion.response.signature),
          userHandle: new Uint8Array(assertion.response.userHandle),
        });

      if (verifyError) {
        console.error('Verify error:', verifyError);
        throw new Error('Passkey verification failed. Please try again.');
      }

      // Get the session
      const { data: { session }, error: sessionError } = 
        await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw new Error('Failed to retrieve session.');
      }
      
      if (session) {
        setSuccess('Passkey login successful! Redirecting...');
        setTimeout(() => {
          handleSuccessfulLogin(session.user.id, session.user.email);
        }, 500);
      } else {
        setError('No session found after passkey verification.');
      }

    } catch (error) {
      console.error('Passkey login error:', error);
      if (error.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled or denied.');
      } else if (error.name === 'NotSupportedError') {
        setError('Passkeys are not supported on this device.');
      } else {
        setError('Passkey login failed: ' + error.message);
      }
    } finally {
      setPasskeyLoading(false);
    }
  }

  const handleRegisterPasskey = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    if (!checkPasskeySupport()) {
      setError('Your browser does not support passkeys. Please use email/password or Google login.');
      return;
    }

    setPasskeyLoading(true);
    setError('');
    setSuccess('');

    try {
      // Check if user already exists
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .maybeSingle();

      if (!existingUser) {
        setError('No account found with this email. Please create an account first.');
        setPasskeyLoading(false);
        return;
      }

      // Get the user's current session or sign them in
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // If no session, they need to login first
        setError('Please login with your password first to register a passkey.');
        setPasskeyLoading(false);
        return;
      }

      // Enroll passkey
      const { data: credentialData, error: credentialError } = 
        await supabase.auth.mfa.enroll({
          factorType: 'webauthn',
          friendlyName: `Passkey for ${email}`,
        });

      if (credentialError) {
        console.error('Enroll error:', credentialError);
        throw new Error('Failed to start passkey registration.');
      }

      // WebAuthn creation
      const publicKeyCredentialCreationOptions = {
        challenge: Uint8Array.from(atob(credentialData.challenge), c => c.charCodeAt(0)),
        rp: {
          name: 'EstatesFlow CRM',
          id: window.location.hostname,
        },
        user: {
          id: new TextEncoder().encode(session.user.id),
          name: email,
          displayName: email.split('@')[0],
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },  // ES256
          { type: 'public-key', alg: -257 }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          residentKey: 'preferred',
          userVerification: 'required',
        },
        timeout: 60000,
        attestation: 'none',
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });

      // Verify credential
      const { data, error: verifyError } = await supabase.auth.mfa.verify({
        factorId: credentialData.id,
        challengeId: credentialData.id,
        authenticatorData: new Uint8Array(credential.response.authenticatorData),
        clientDataJSON: new Uint8Array(credential.response.clientDataJSON),
        signature: new Uint8Array(credential.response.signature),
        userHandle: new Uint8Array(credential.response.userHandle),
      });

      if (verifyError) {
        console.error('Verify error:', verifyError);
        throw new Error('Passkey verification failed. Please try again.');
      }

      setSuccess('✅ Passkey registered successfully! You can now login with your passkey.');
      setTimeout(() => {
        setIsPasskeyMode(false);
        setPasskeyStep('login');
      }, 2000);
      
    } catch (error) {
      console.error('Passkey registration error:', error);
      if (error.name === 'NotAllowedError') {
        setError('Passkey registration was cancelled or denied.');
      } else if (error.name === 'NotSupportedError') {
        setError('Passkeys are not supported on this device.');
      } else {
        setError('Passkey registration failed: ' + error.message);
      }
    } finally {
      setPasskeyLoading(false);
    }
  }

  // ============ EXISTING FUNCTIONS ============
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    if (isForgotPassword) {
      try {
        const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `https://estatesflow.online/reset-password`,
        })
        
        if (error) {
          setError(error.message)
          setLoading(false)
          return
        }
        
        let accessToken = null
        if (data?.url) {
          const hashMatch = data.url.match(/#access_token=([^&]+)/)
          if (hashMatch) {
            accessToken = hashMatch[1]
          }
        }
        
        let resetLink
        if (accessToken) {
          resetLink = `https://estatesflow.online/reset-password#access_token=${accessToken}&type=recovery`
        } else {
          resetLink = data?.url || `https://estatesflow.online/reset-password`
        }
        
        const emailResult = await sendPasswordResetEmail(email, resetLink, 'EstatesFlow')
        
        if (emailResult.success) {
          setResetSent(true)
          setSuccess('Password reset email sent! Check your inbox.')
        } else {
          setError('Failed to send reset email. Please try again.')
        }
      } catch (err) {
        console.error('Reset error:', err)
        setError('Unable to send reset link. Please try again later.')
      }
      setLoading(false)
      return
    }

    if (isSignUp) {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `https://estatesflow.online/auth/callback`,
          data: {
            full_name: email.split('@')[0],
            onboarding_completed: false
          }
        }
      })
      
      if (error) {
        setError(error.message)
      } else {
        setSuccess('Verification email sent! Please check your inbox (and spam folder).')
        if (data.user?.email_confirmed_at) {
          const { data: userData } = await supabase
            .from('users')
            .select('role')
            .eq('id', data.user.id)
            .maybeSingle()
          
          if (!userData?.role) {
            navigate('/onboarding')
          } else {
            await handleSuccessfulLogin(data.user.id, email)
          }
        }
      }
    } else {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) {
        setError(error.message)
      } else {
        setSuccess('Login successful! Redirecting...')
        setTimeout(() => {
          handleSuccessfulLogin(data.user.id, email)
        }, 500)
      }
    }
    setLoading(false)
  }

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setError('')
    
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `https://estatesflow.online/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          scopes: 'email profile'
        }
      })
      
      if (error) {
        console.error('Google sign in error:', error)
        throw error
      }
    } catch (err) {
      console.error('Google sign in error:', err)
      if (err.message === 'invalid_client') {
        setError('Google Sign-In is not configured. Please contact support or use email/password.')
      } else {
        setError(err.message || 'Failed to sign in with Google')
      }
      setLoading(false)
    }
  }

  const goToLanding = () => {
    navigate('/')
  }

  const backToLogin = () => {
    setIsForgotPassword(false)
    setResetSent(false)
    setEmail('')
    setError('')
    setSuccess('')
    setIsPasskeyMode(false)
    setPasskeyStep('login')
  }

  const switchToPasskeyLogin = () => {
    setIsPasskeyMode(true)
    setPasskeyStep('login')
    setError('')
    setSuccess('')
  }

  const switchToPasskeyRegister = () => {
    setIsPasskeyMode(true)
    setPasskeyStep('register')
    setError('')
    setSuccess('')
  }

  // ============ RENDER ============
  if (resetSent) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <button onClick={goToLanding} style={styles.backHome}>← Back to Home</button>
          <div style={styles.icon}>📧</div>
          <h1 style={styles.title}>Check Your Email</h1>
          <p style={styles.text}>We've sent a password reset link to <strong>{email}</strong></p>
          <p style={{ ...styles.text, fontSize: '12px', marginTop: '10px' }}>
            Click the link in the email to reset your password. Check your spam folder if you don't see it.
          </p>
          <button onClick={backToLogin} style={styles.backBtn}>
            ← Back to Login
          </button>
        </div>
      </div>
    )
  }

  if (isForgotPassword) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <button onClick={goToLanding} style={styles.backHome}>← Back to Home</button>
          <div style={styles.icon}>🔐</div>
          <h1 style={styles.title}>Reset Password</h1>
          <p style={styles.text}>Enter your email to receive a reset link.</p>
          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}
          <form onSubmit={handleSubmit}>
            <input 
              type="email" 
              placeholder="Email address" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              style={styles.input} 
              required 
            />
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
          <button onClick={backToLogin} style={styles.linkBtn}>
            ← Back to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <button onClick={goToLanding} style={styles.backHome}>← Back to Home</button>
        
        <div style={styles.logo}>🏢</div>
        <h1 style={styles.title}>EstatesFlow CRM</h1>
        <p style={styles.subtitle}>Complete Real Estate Management</p>
        
        <div style={styles.tabContainer}>
          <button 
            onClick={() => { setIsSignUp(false); setError(''); setSuccess(''); setIsPasskeyMode(false) }}
            style={!isSignUp && !isPasskeyMode ? styles.activeTab : styles.tab}
          >
            Sign In
          </button>
          <button 
            onClick={() => { setIsSignUp(true); setError(''); setSuccess(''); setIsPasskeyMode(false) }}
            style={isSignUp && !isPasskeyMode ? styles.activeTab : styles.tab}
          >
            Create Account
          </button>
          <button 
            onClick={() => { setIsPasskeyMode(true); setIsSignUp(false); setError(''); setSuccess(''); setPasskeyStep('login') }}
            style={isPasskeyMode ? styles.activeTab : styles.tab}
          >
            🔑 Passkey
          </button>
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        {isPasskeyMode ? (
          // ============ PASSKEY MODE ============
          <div>
            <p style={styles.passkeyInfo}>
              {checkPasskeySupport() 
                ? '🔐 Use Face ID, Touch ID, or your device PIN to authenticate.'
                : '⚠️ Passkeys are not supported in this browser. Use email/password or Google login.'}
            </p>
            
            <div style={styles.passkeyEmailContainer}>
              <input 
                type="email" 
                placeholder="Email address" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                style={styles.input} 
                required 
              />
            </div>

            {/* Passkey mode switcher */}
            <div style={styles.passkeyModeSwitcher}>
              <button 
                onClick={() => setPasskeyStep('login')}
                style={passkeyStep === 'login' ? styles.passkeyActiveMode : styles.passkeyInactiveMode}
              >
                Login with Passkey
              </button>
              <button 
                onClick={() => setPasskeyStep('register')}
                style={passkeyStep === 'register' ? styles.passkeyActiveMode : styles.passkeyInactiveMode}
              >
                Register Passkey
              </button>
            </div>

            {passkeyStep === 'login' ? (
              <button 
                onClick={handlePasskeyLogin} 
                disabled={passkeyLoading || !email} 
                style={styles.passkeyLoginBtn}
              >
                {passkeyLoading ? 'Authenticating...' : '🔑 Login with Passkey'}
              </button>
            ) : (
              <div>
                <p style={styles.passkeyRegisterInfo}>
                  Register a passkey for quick, passwordless login using Face ID, Touch ID, or your device PIN.
                </p>
                <button 
                  onClick={handleRegisterPasskey} 
                  disabled={passkeyLoading || !email} 
                  style={styles.passkeyRegisterBtn}
                >
                  {passkeyLoading ? 'Registering...' : '➕ Register New Passkey'}
                </button>
              </div>
            )}

            <button 
              onClick={backToLogin} 
              style={styles.linkBtn}
            >
              ← Back to Login Options
            </button>
          </div>
        ) : (
          // ============ EMAIL/PASSWORD MODE ============
          <>
            <form onSubmit={handleSubmit}>
              <input 
                type="email" 
                placeholder="Email address" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                style={styles.input} 
                required 
              />
              
              {!isForgotPassword && (
                <div style={styles.passwordContainer}>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    placeholder={isSignUp ? "Create a password (min 6 characters)" : "Password"} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    style={styles.passwordInput} 
                    required 
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.passwordToggle}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              )}
              
              {!isForgotPassword && (
                <button type="submit" disabled={loading} style={styles.button}>
                  {loading ? 'Loading...' : (isSignUp ? 'Create Account' : 'Sign In')}
                </button>
              )}
            </form>

            {!isSignUp && !isForgotPassword && (
              <button onClick={() => setIsForgotPassword(true)} style={styles.forgotBtn}>
                Forgot password?
              </button>
            )}

            {!isSignUp && !isForgotPassword && (
              <button onClick={switchToPasskeyLogin} style={styles.passkeySwitchBtn}>
                🔑 Use Passkey Instead
              </button>
            )}

            {!isForgotPassword && (
              <>
                <div style={styles.divider}>
                  <span style={styles.dividerLine}></span>
                  <span style={styles.dividerText}>or</span>
                  <span style={styles.dividerLine}></span>
                </div>

                <button 
                  onClick={handleGoogleSignIn} 
                  disabled={loading} 
                  style={styles.googleBtn}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continue with Google
                </button>
              </>
            )}
          </>
        )}

        <div style={styles.footer}>
          <p style={styles.footerText}>
            © {new Date().getFullYear()} EstatesFlow CRM<br />
            Complete Real Estate Management Platform
          </p>
        </div>
      </div>
    </div>
  )
}

// ============ STYLES ============
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Inter', system-ui, sans-serif"
  },
  card: {
    background: '#fff',
    borderRadius: '24px',
    padding: '40px',
    width: '100%',
    maxWidth: '450px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    position: 'relative'
  },
  backHome: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  logo: { fontSize: '48px', marginBottom: '16px', textAlign: 'center' },
  icon: { fontSize: '48px', marginBottom: '16px', textAlign: 'center' },
  title: { fontSize: '28px', fontWeight: '700', color: '#1e3a5f', marginBottom: '8px', textAlign: 'center' },
  subtitle: { fontSize: '12px', color: '#64748b', textAlign: 'center', marginBottom: '24px' },
  text: { fontSize: '16px', color: '#64748b', marginBottom: '20px', textAlign: 'center' },
  tabContainer: {
    display: 'flex',
    gap: '6px',
    marginBottom: '24px',
    background: '#f1f5f9',
    padding: '4px',
    borderRadius: '12px'
  },
  tab: {
    flex: 1,
    padding: '10px 12px',
    border: 'none',
    background: 'transparent',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    color: '#64748b',
    transition: 'all 0.2s'
  },
  activeTab: {
    flex: 1,
    padding: '10px 12px',
    border: 'none',
    background: '#fff',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    color: '#1e3a5f',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    margin: '8px 0',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '16px',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  passwordContainer: {
    position: 'relative',
    width: '100%',
    margin: '8px 0'
  },
  passwordInput: {
    width: '100%',
    padding: '14px 16px',
    paddingRight: '70px',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '16px',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  passwordToggle: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#64748b',
    padding: '4px 8px',
    borderRadius: '6px'
  },
  button: {
    width: '100%',
    padding: '14px',
    marginTop: '16px',
    background: '#1e3a5f',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  googleBtn: {
    width: '100%',
    padding: '12px',
    background: '#fff',
    color: '#1e293b',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  passkeyInfo: {
    fontSize: '14px',
    color: '#64748b',
    textAlign: 'center',
    marginBottom: '16px',
    padding: '12px',
    background: '#f1f5f9',
    borderRadius: '8px'
  },
  passkeyRegisterInfo: {
    fontSize: '13px',
    color: '#64748b',
    textAlign: 'center',
    marginBottom: '16px',
    padding: '10px',
    background: '#f0fdf4',
    borderRadius: '8px',
    border: '1px solid #d1fae5'
  },
  passkeyEmailContainer: {
    marginBottom: '12px'
  },
  passkeyModeSwitcher: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    background: '#f1f5f9',
    padding: '4px',
    borderRadius: '10px'
  },
  passkeyActiveMode: {
    flex: 1,
    padding: '8px 12px',
    background: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    color: '#1e3a5f',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  passkeyInactiveMode: {
    flex: 1,
    padding: '8px 12px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    color: '#64748b'
  },
  passkeyButtonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '8px'
  },
  passkeyLoginBtn: {
    width: '100%',
    padding: '14px',
    background: '#1e3a5f',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  passkeyRegisterBtn: {
    width: '100%',
    padding: '14px',
    background: '#059669',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  passkeySwitchBtn: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: '13px',
    cursor: 'pointer',
    marginTop: '10px',
    width: '100%',
    padding: '8px',
    borderRadius: '8px',
    transition: 'background 0.2s'
  },
  error: { background: '#fee2e2', color: '#dc2626', padding: '12px', borderRadius: '12px', marginBottom: '16px', fontSize: '14px' },
  success: { background: '#d1fae5', color: '#059669', padding: '12px', borderRadius: '12px', marginBottom: '16px', fontSize: '14px' },
  divider: { display: 'flex', alignItems: 'center', gap: '16px', margin: '24px 0' },
  dividerLine: { flex: 1, height: '1px', background: '#e2e8f0' },
  dividerText: { color: '#94a3b8', fontSize: '14px' },
  linkBtn: { background: 'none', border: 'none', color: '#2c7da0', fontSize: '14px', cursor: 'pointer', marginTop: '16px', width: '100%' },
  forgotBtn: { background: 'none', border: 'none', color: '#64748b', fontSize: '13px', cursor: 'pointer', marginTop: '12px', width: '100%' },
  backBtn: { background: 'none', border: 'none', color: '#2c7da0', fontSize: '14px', cursor: 'pointer', marginTop: '20px', width: '100%' },
  footer: {
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
    textAlign: 'center'
  },
  footerText: {
    fontSize: '10px',
    color: '#94a3b8',
    margin: '0',
    lineHeight: '1.4'
  }
}

export default Login